import { DeleteOutlined } from '@ant-design/icons'
import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Typography,
} from 'antd'
import {
  TwoStepDropdown,
  TwoStepFieldSelectOption,
} from 'features/drilldown/components/fieldSelect/components/TwoStepSelect'
import { DrilldownTypeSelect } from 'features/drilldown/components/fieldSelect/TypeSelect'
import {
  OntologyRolodexEntryType,
  toTitleCase,
  useOntology,
} from 'features/dynamicOntology'
import snakeCase from 'lodash/snakeCase'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { EquationVisualization } from './EquationVisualization'
import {
  CALCULATION_TYPES_WITHOUT_FIELDS,
  DrilldownMetricConfig,
  ExpressionType,
  FIELD_OPERATOR_META,
  FieldExpression,
  FieldOperator,
  MetricCalculationType,
} from './MetricsPanelConfig'

const { TextArea } = Input
const { Title } = Typography

interface CreateMetricModalProps {
  open: boolean
  onClose: () => void
  onSave: (config: DrilldownMetricConfig) => Promise<void>
  existingConfig: DrilldownMetricConfig | null
  title?: string
}

const CALCULATION_TYPE_OPTIONS: ReadonlyArray<{
  label: string
  value: MetricCalculationType
}> = Object.values(MetricCalculationType).map((value) => ({
  label: toTitleCase(value),
  value,
}))

const OPERATION_OPTIONS: ReadonlyArray<{
  label: string
  value: FieldOperator
}> = Object.values(FieldOperator).map((value) => ({
  label: FIELD_OPERATOR_META[value].label,
  value,
}))

export const CreateMetricModal: FC<CreateMetricModalProps> = ({
  open,
  onClose,
  onSave,
  existingConfig,
  title = 'Create Metric',
}) => {
  const [form] = Form.useForm()
  const { ontology, entryTypeNames } = useOntology()

  const [selectedRolodexType, setSelectedRolodexType] =
    useState<OntologyRolodexEntryType>('')

  const [fieldExpressions, setFieldExpressions] = useState<
    ReadonlyArray<FieldExpression>
  >([])
  const [fieldDropdownPrimary, setFieldDropdownPrimary] = useState<string>('')
  const [fieldDropdownSecondary, setFieldDropdownSecondary] =
    useState<string>('')

  const [isBuildingGroup, setIsBuildingGroup] = useState<boolean>(false)
  const [currentGroupExpressions, setCurrentGroupExpressions] = useState<
    ReadonlyArray<FieldExpression>
  >([])
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // - - - - - - - - - - - Helper functions for form and state - - - - - - - - - - -
  const resetFormAndState = useCallback(() => {
    form.resetFields()
    setSelectedRolodexType('')
    setFieldExpressions([])
    setFieldDropdownPrimary('')
    setFieldDropdownSecondary('')
    setIsBuildingGroup(false)
    setCurrentGroupExpressions([])
  }, [form])

  // Initialize form with initial config when editing
  useEffect(() => {
    if (open && existingConfig) {
      form.setFieldsValue({
        title: existingConfig.title,
        metricType: existingConfig.metricType,
        calculationType: existingConfig.calculationType,
        numDecimals: existingConfig.numDecimals,
        suffix: existingConfig.suffix,
        description: existingConfig.description,
      })
      setSelectedRolodexType(existingConfig.rolodexType)
      setFieldExpressions(existingConfig.fieldExpressions || [])
    } else if (open) {
      resetFormAndState()
    }
  }, [open, existingConfig, form, resetFormAndState])

  const fieldOptions = useMemo<{
    primaryOptions: ReadonlyArray<TwoStepFieldSelectOption>
    secondaryOptions: ReadonlyMap<
      string,
      ReadonlyArray<TwoStepFieldSelectOption>
    >
  }>(() => {
    const primaryOptions = Object.keys(ontology.types).map((entryType) => ({
      label: entryTypeNames[entryType] ?? toTitleCase(entryType),
      value: entryType,
    }))

    const secondaryOptions = new Map(
      Object.entries(ontology.types).map(([entryType, typeDef]) => [
        entryType,
        // Field values are the full `type.path` keys used by stored metric
        // configs and the flattened ontology field lookup
        Object.entries(typeDef.fields).map(([fieldPath, fieldDef]) => ({
          label: fieldDef.name,
          value: `${entryType}.${fieldPath}`,
        })),
      ])
    )

    return { primaryOptions, secondaryOptions }
  }, [ontology, entryTypeNames])

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields()

      const config: DrilldownMetricConfig = {
        metricType: snakeCase(values.title),
        rolodexType: selectedRolodexType,
        title: values.title,
        calculationType: values.calculationType,
        fieldExpressions:
          fieldExpressions.length > 0 ? fieldExpressions : undefined,
        suffix: values.suffix || undefined,
        numDecimals: values.numDecimals || undefined,
        description: values.description || undefined,
      }

      setIsSaving(true)
      try {
        await onSave(config)
      } finally {
        setIsSaving(false)
      }
      resetFormAndState()
      onClose()
    } catch (error) {
      console.error('Form validation failed:', error)
    }
  }, [
    form,
    onSave,
    onClose,
    selectedRolodexType,
    fieldExpressions,
    resetFormAndState,
  ])

  const handleCancel = useCallback(() => {
    resetFormAndState()
    onClose()
  }, [resetFormAndState, onClose])

  // - - - - - - - - - - - Helper functions for field expression builder - - - - - - - - - - -
  // Watches for whether the calculation type needs field expressions
  const calculationType = Form.useWatch('calculationType', form)
  const needsFieldExpressions =
    calculationType &&
    !CALCULATION_TYPES_WITHOUT_FIELDS.includes(
      calculationType as (typeof CALCULATION_TYPES_WITHOUT_FIELDS)[number]
    )

  const startGroup = useCallback(() => {
    setIsBuildingGroup(true)
    setCurrentGroupExpressions([])
  }, [])

  const endCurrentGroup = useCallback(() => {
    if (isBuildingGroup && currentGroupExpressions.length > 0) {
      setFieldExpressions((prev) => [
        ...prev,
        { type: ExpressionType.GROUP, expressions: currentGroupExpressions },
      ])
      setCurrentGroupExpressions([])
    }
    setIsBuildingGroup(false)
  }, [isBuildingGroup, currentGroupExpressions])

  const cancelCurrentGroup = useCallback(() => {
    setIsBuildingGroup(false)
    setCurrentGroupExpressions([])
  }, [])

  const addFieldExpression = useCallback(
    (expression: FieldExpression) => {
      if (isBuildingGroup) {
        setCurrentGroupExpressions((prev) => [...prev, expression])
      } else {
        setFieldExpressions((prev) => [...prev, expression])
      }
    },
    [isBuildingGroup]
  )

  const addFieldValue = useCallback(
    (field: string) => {
      const fieldExpr: FieldExpression = { type: ExpressionType.FIELD, field }
      addFieldExpression(fieldExpr)
    },
    [addFieldExpression]
  )

  const addOperationValue = useCallback(
    (operation: string) => {
      const operationExpr: FieldExpression = {
        type: ExpressionType.OPERATION,
        operation: operation as FieldOperator,
      }
      addFieldExpression(operationExpr)
    },
    [addFieldExpression]
  )

  const handleFieldSelected = useCallback<Dispatch<SetStateAction<string>>>(
    (field) => {
      setFieldDropdownSecondary(field)
      if (typeof field === 'string' && field) {
        addFieldValue(field)
        // Reset the dropdown after adding
        setFieldDropdownPrimary('')
        setFieldDropdownSecondary('')
      }
    },
    [addFieldValue]
  )

  const clearExpressions = useCallback(() => {
    setFieldExpressions([])
    setIsBuildingGroup(false)
    setCurrentGroupExpressions([])
  }, [])

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key='cancel' onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key='save'
          type='primary'
          onClick={handleSave}
          loading={isSaving}>
          Save Metric
        </Button>,
      ]}>
      <Form form={form} layout='vertical' requiredMark={false}>
        <Title level={5}>Basic Information</Title>

        <Form.Item
          name='title'
          label='Metric Title'
          rules={[{ required: true, message: 'Please enter a metric title' }]}>
          <Input placeholder='e.g., Total Indication Count' />
        </Form.Item>

        <Form.Item name='description' label='Description'>
          <TextArea
            placeholder='Optional description of what this metric calculates'
            rows={2}
          />
        </Form.Item>

        <Form.Item label='Data Source'>
          <DrilldownTypeSelect
            selectedType={selectedRolodexType}
            setSelectedType={setSelectedRolodexType}
            types={fieldOptions.primaryOptions.map((opt) => opt.value)}
            placeholder='Select the type of data to analyze'
            width='400px'
          />
        </Form.Item>

        <Divider />

        <Title level={5}>Calculation Settings</Title>

        <Form.Item
          name='calculationType'
          label='Calculation Type'
          rules={[
            { required: true, message: 'Please select a calculation type' },
          ]}>
          <Select
            placeholder='How to aggregate the data'
            options={[...CALCULATION_TYPE_OPTIONS]}
          />
        </Form.Item>

        <Space orientation='horizontal' style={{ width: '100%' }}>
          <Form.Item name='suffix' label='Unit Suffix' style={{ flex: 1 }}>
            <Input placeholder='e.g., mm, %' />
          </Form.Item>
          <Form.Item
            name='numDecimals'
            label='Decimal Places'
            style={{ flex: 1 }}>
            <InputNumber min={0} max={10} placeholder='2' />
          </Form.Item>
        </Space>

        {needsFieldExpressions && (
          <>
            <Divider />

            <Title level={5}>Field Expression Builder</Title>
            <Typography.Text type='secondary'>
              Build mathematical expressions using fields and operations
            </Typography.Text>

            <EquationVisualization
              expressions={fieldExpressions}
              title='Current Expression'
              currentGroup={
                isBuildingGroup ? currentGroupExpressions : undefined
              }
            />

            <div>
              <Typography.Text strong>Grouping:</Typography.Text>
              <Space style={{ width: '100%' }}>
                {!isBuildingGroup ? (
                  <Button
                    onClick={startGroup}
                    icon={<span style={{ fontWeight: 'bold' }}>(</span>}>
                    Start Group
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={endCurrentGroup}
                      disabled={currentGroupExpressions.length === 0}
                      type='primary'
                      icon={<span style={{ fontWeight: 'bold' }}>)</span>}>
                      End Group
                    </Button>
                    <Button onClick={cancelCurrentGroup} danger>
                      Cancel Group
                    </Button>
                  </>
                )}
              </Space>
              {isBuildingGroup && (
                <Typography.Text
                  type='secondary'
                  style={{
                    fontSize: '12px',
                    display: 'block',
                    marginTop: '4px',
                  }}>
                  Building group ({currentGroupExpressions.length} items). Add
                  fields/operations, then click &quot;End Group&quot;.
                </Typography.Text>
              )}
            </div>

            <Space
              orientation='vertical'
              style={{ width: '100%', marginTop: 16 }}>
              <div>
                <Typography.Text strong>Add Field:</Typography.Text>
                <TwoStepDropdown
                  primaryOptions={fieldOptions.primaryOptions}
                  secondaryOptions={fieldOptions.secondaryOptions}
                  primarySelection={fieldDropdownPrimary}
                  setPrimarySelection={setFieldDropdownPrimary}
                  secondarySelection={fieldDropdownSecondary}
                  setSecondarySelection={handleFieldSelected}
                  placeholder='Select field to add'
                  width='100%'
                />
              </div>

              <div>
                <Typography.Text strong>Add Operation:</Typography.Text>
                <Select
                  placeholder='Select operation to add'
                  options={[...OPERATION_OPTIONS]}
                  style={{ width: '100%' }}
                  onSelect={(operation) => {
                    addOperationValue(operation)
                  }}
                />
              </div>

              <Space>
                <Button
                  onClick={() =>
                    setFieldExpressions((prev) => prev.slice(0, -1))
                  }
                  disabled={fieldExpressions.length === 0}
                  icon={<DeleteOutlined />}>
                  Remove Last
                </Button>
                <Button
                  onClick={clearExpressions}
                  disabled={fieldExpressions.length === 0}
                  danger>
                  Clear All
                </Button>
              </Space>
            </Space>
          </>
        )}
      </Form>
    </Modal>
  )
}
